import { fn, get, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { camelize } from '@ember/string';
import { importSync } from '@embroider/macros';

import can from 'ember-can/helpers/can';
import cannot from 'ember-can/helpers/cannot';
import { t } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, not } from 'ember-truth-helpers';

import Button from 'umg-ember-backend/components/ui/button/index';
import Modal from 'umg-ember-backend/components/ui/modal/index';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import Component from '../has-many';
import styles from '../has-many/styles.module.css';

const bem = getBem(styles);

export default class UiFormFieldHasManyJoinComponent extends Component {
  // Services
  @service store;

  // Getter and setter
  get RecordExplorerForAssign() {
    const type = this.args.foreignModelName ?? 'article';

    const module = importSync(`../../../../record-explorer/for/${type}`);

    return module.default;
  }

  // Functions
  setRelation = (records) => {
    let model = this.args.model;
    let attributeLeft = camelize(this.args.recordType);
    let attributeRight = camelize(this.args.foreignModelName);

    records.forEach((record) => {
      let joinRelation = this.store.createRecord(this.relationType);

      joinRelation[attributeLeft] = model;
      joinRelation[attributeRight] = record;
    });
  };

  // Template
  <template>
    <div
      class={{bem (hash is-sortable=this.sortableNotDisabled)}}
      id={{@fieldId}}
      ...attributes
    >
      {{#if this.sortedRecords}}
        <ol
          class={{bem 'records'}}
          {{sortableGroup
            disabled=(not this.sortableNotDisabled)
            groupName=this.sortableGroup
            onChange=this.reorderItems
          }}
        >
          {{#each this.sortedRecords as |record index|}}
            <li
              class={{bem 'record'}}
              {{sortableItem groupName=this.sortableGroup model=record}}
            >
              <div
                class={{bem
                  'wrapper'
                  (hash
                    has-errors=(and @showErrors record.validations.isInvalid)
                  )
                }}
              >
                <div class={{bem 'handle'}} {{sortableHandle}}>
                  {{svgJar 'drag' class=(bem 'drag-indicator')}}
                </div>
                <div class={{bem 'value'}}>
                  {{#if (has-block)}}
                    {{yield record this.labelProperty index}}
                  {{else}}
                    {{get record this.labelProperty}}
                  {{/if}}
                </div>
              </div>
              <div class={{bem 'actions'}}>
                {{#if this.on.openRecord}}
                  <Button
                    @disabled={{cannot
                      'read'
                      @foreignModelName
                      parentModel=@model
                    }}
                    @style='outline'
                    @icon='edit'
                    class={{bem 'open-record'}}
                    {{on
                      'click'
                      (fn this.on.openRecord (get record @foreignModelName))
                    }}
                  />
                {{/if}}
                {{#if (and this.on.removeRecord (can 'update' @recordType))}}
                  <Button
                    @style='outline'
                    @disabled={{@isDisabled}}
                    @icon='delete'
                    @iconClass='u-color-error'
                    class={{bem 'remove-record'}}
                    {{on
                      'click'
                      (fn
                        this.on.removeRecord
                        record
                        @model
                        this.relationKind
                        @property
                      )
                    }}
                  />
                {{/if}}
              </div>
            </li>
          {{/each}}
        </ol>
      {{/if}}
      {{#if (can 'update' @recordType)}}
        <div class={{bem 'add-new'}}>
          {{#if this.on.openRecordList}}
            <Button
              @style='outline'
              @disabled={{@isDisabled}}
              @icon='search'
              {{on
                'click'
                (fn this.on.openRecordList @foreignModelName @property)
              }}
            />
          {{/if}}
          {{#if
            (and
              this.on.openNewRecord
              (can 'create' @foreignModelName parentModel=@model)
            )
          }}
            <Button
              @style='outline'
              @disabled={{@isDisabled}}
              @icon='add'
              @label={{t 'ui.form.field.has_many.new'}}
              {{on
                'click'
                (fn
                  this.on.openNewRecord
                  @model
                  this.relationKind
                  @foreignModelName
                  @property
                  @filters
                )
              }}
            />
          {{/if}}
        </div>
      {{/if}}
    </div>
    <Modal
      @onClose={{this.closeRecordList}}
      @dataDependencies={{this.isRecordListOpen}}
    >
      <this.RecordExplorerForAssign
        @filters={{@filters}}
        @onAssignRelation={{this.on.assignRecords}}
        @recordType={{@foreignModelName}}
        @options={{@options}}
        @parentRecordType={{getRecordType @model}}
        @relationType='hasMany'
        @scrollPosition={{this.scrollPositionAssign}}
        @onBodyScroll={{fn (mut this.scrollPositionAssign)}}
      />
    </Modal>
  </template>
}
